import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Download, Save, Mail, Upload, X, Receipt, History } from 'lucide-react';
import QuoteVersionHistory from '@/components/QuoteVersionHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [costing, setCosting] = useState(null);
  const [quote, setQuote] = useState({
    quote_number: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    line_items: [],
    notes: '',
    attachments: [],
    is_rush_job: false,
    date_quoted: new Date().toISOString().split('T')[0],
  });
  const [saving, setSaving] = useState(false);
  const [lastSavedQuoteId, setLastSavedQuoteId] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showLoadQuotes, setShowLoadQuotes] = useState(false);
  const [previousQuotes, setPreviousQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [convertingToInvoice, setConvertingToInvoice] = useState(false);
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState(null);
  const [aiDataLoaded, setAiDataLoaded] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  useEffect(() => {
    loadCosting();
    generateQuoteNumber();
    loadEstimateAndAIData();
  }, []);

  const loadPreviousQuotes = async () => {
    setLoadingQuotes(true);
    try {
      const response = await axios.get(`${API}/quotes`);
      setPreviousQuotes(response.data);
    } catch (error) {
      toast.error('Failed to load previous quotes');
    } finally {
      setLoadingQuotes(false);
    }
  };

  const loadCosting = async () => {
    try {
      const response = await axios.get(`${API}/costing`);
      setCosting(response.data);
    } catch (error) {
      toast.error('Failed to load costing settings');
    }
  };

  const generateQuoteNumber = async () => {
    try {
      const response = await axios.get(`${API}/quotes/next-number`);
      setQuote(prev => ({ ...prev, quote_number: response.data.quote_number }));
    } catch (error) {
      console.error('Failed to generate quote number');
    }
  };

  const loadEstimateAndAIData = async () => {
    const searchParams = new URLSearchParams(location.search);
    const estimateId = searchParams.get('estimate_id');
    const aiDataParam = searchParams.get('ai_data');
    
    try {
      // Load estimate data first
      if (estimateId) {
        console.log('Loading estimate:', estimateId);
        const estimateResponse = await axios.get(`${API}/estimates/requests/${estimateId}`);
        const estimate = estimateResponse.data;
        
        // Try to get customer details from the estimate's customer_id
        let customerName = estimate.customer_name || '';
        let customerEmail = estimate.customer_email || '';
        let customerPhone = '';
        let customerAddress = '';
        
        if (estimate.customer_id) {
          try {
            // Try to get full customer profile
            const customerResponse = await axios.get(`${API}/users/${estimate.customer_id}`);
            const customer = customerResponse.data;
            customerName = customer.name || customerName;
            customerEmail = customer.email || customerEmail;
            customerPhone = customer.phone || '';
            customerAddress = customer.address || '';
          } catch (err) {
            console.log('Could not fetch customer details, using estimate data');
          }
        }
        
        // Pre-populate customer info
        setQuote(prev => ({
          ...prev,
          client_name: customerName,
          client_email: customerEmail,
          client_phone: customerPhone,
          client_address: customerAddress,
          notes: `Project: ${estimate.project_name || 'N/A'}\nType: ${estimate.project_type || 'N/A'}\nDescription: ${estimate.description || 'N/A'}`
        }));
        
        console.log('Customer info loaded');
      }
      
      // Load AI recommendations if provided
      if (aiDataParam) {
        try {
          const aiData = JSON.parse(decodeURIComponent(aiDataParam));
          console.log('AI Data received:', aiData);
          
          // Map AI data to proper quote line items by category
          const lineItems = [];
          
          // Labour items with milestones
          if (aiData.labour_items && aiData.labour_items.length > 0) {
            aiData.labour_items.forEach(item => {
              lineItems.push({
                category: 'Labour',
                description: item.milestone ? `[${item.milestone}] ${item.description}` : item.description,
                hours: item.hours || 0,
                rate: item.rate || 0,
                total: item.total || (item.hours * item.rate)
              });
            });
          }
          
          // Design/Engineering items
          if (aiData.design_items && aiData.design_items.length > 0) {
            aiData.design_items.forEach(item => {
              lineItems.push({
                category: 'Design',
                description: item.description,
                hours: item.hours || 0,
                rate: item.rate || 0,
                total: item.total || (item.hours * item.rate)
              });
            });
          }
          
          // Material items
          if (aiData.material_items && aiData.material_items.length > 0) {
            aiData.material_items.forEach(item => {
              lineItems.push({
                category: 'Materials',
                description: item.unit ? `${item.description} (${item.unit})` : item.description,
                quantity: item.quantity || 0,
                unit_cost: item.unit_cost || 0,
                total: item.total || (item.quantity * item.unit_cost)
              });
            });
          }
          
          // Tool items
          if (aiData.tool_items && aiData.tool_items.length > 0) {
            aiData.tool_items.forEach(item => {
              lineItems.push({
                category: 'Tools',
                description: item.description,
                hours: item.hours || 0,
                quantity: item.quantity || 1,
                unit_cost: item.unit_cost || 0,
                total: item.total || (item.hours * item.quantity * item.unit_cost)
              });
            });
          }
          
          // Shop/Admin items (certifications, project management)
          if (aiData.shop_admin_items && aiData.shop_admin_items.length > 0) {
            aiData.shop_admin_items.forEach(item => {
              lineItems.push({
                category: 'Shop/Admin',
                description: item.description,
                quantity: item.quantity || 1,
                unit_cost: item.unit_cost || 0,
                total: item.total || (item.quantity * item.unit_cost)
              });
            });
          }
          
          // Add value-adds from sales strategy as separate items (staff can price)
          if (aiData.sales_strategy?.value_adds && aiData.sales_strategy.value_adds.length > 0) {
            aiData.sales_strategy.value_adds.forEach(valueAdd => {
              lineItems.push({
                category: 'Materials',
                description: `✓ VALUE-ADD: ${valueAdd}`,
                quantity: 1,
                unit_cost: 0,
                total: 0
              });
            });
          }
          
          // Build comprehensive notes
          let aiNotes = quote.notes || '';
          
          // Requirements & Specs
          if (aiData.requirements) {
            if (aiData.requirements.technical_specs && aiData.requirements.technical_specs.length > 0) {
              aiNotes += '\n\n[TECHNICAL SPECIFICATIONS]';
              aiData.requirements.technical_specs.forEach(spec => {
                aiNotes += `\n• ${spec}`;
              });
            }
            
            if (aiData.requirements.safety_requirements && aiData.requirements.safety_requirements.length > 0) {
              aiNotes += '\n\n[SAFETY REQUIREMENTS]';
              aiData.requirements.safety_requirements.forEach(req => {
                aiNotes += `\n• ${req}`;
              });
            }
          }
          
          // Timeline
          if (aiData.timeline) {
            aiNotes += '\n\n[ESTIMATED TIMELINE]';
            aiNotes += `\nTotal Hours: ${aiData.timeline.estimated_hours || 'TBD'}`;
            aiNotes += `\nEstimated Days: ${aiData.timeline.estimated_days || 'TBD'}`;
            aiNotes += `\nDelivery: ${aiData.timeline.delivery_date || 'TBD'}`;
          }
          
          // Pricing Summary
          if (aiData.pricing_summary) {
            aiNotes += '\n\n[AI PRICING ANALYSIS]';
            aiNotes += `\nRecommended Price: $${aiData.pricing_summary.recommended_quote_price?.toLocaleString() || 'TBD'}`;
            aiNotes += `\nProfit Margin: ${aiData.pricing_summary.profit_margin_percent}% ($${aiData.pricing_summary.profit_amount?.toLocaleString()})`;
            aiNotes += `\nMarket Range: $${aiData.pricing_summary.market_rate_min?.toLocaleString()} - $${aiData.pricing_summary.market_rate_max?.toLocaleString()}`;
            aiNotes += `\nBudget Rating: ${aiData.pricing_summary.rating} (${aiData.pricing_summary.rating_score}/100)`;
            if (aiData.pricing_summary.rating_explanation) {
              aiNotes += `\nReasoning: ${aiData.pricing_summary.rating_explanation}`;
            }
          }
          
          // Win Strategy
          if (aiData.sales_strategy?.win_strategy) {
            aiNotes += `\n\n[WIN STRATEGY]\n${aiData.sales_strategy.win_strategy}`;
          }
          
          // Questions to Confirm
          if (aiData.questions_for_customer && aiData.questions_for_customer.length > 0) {
            aiNotes += '\n\n[QUESTIONS TO CONFIRM WITH CUSTOMER]';
            aiData.questions_for_customer.forEach((q, idx) => {
              aiNotes += `\n${idx + 1}. [${q.priority}] ${q.question}`;
            });
          }
          
          // Upsell Opportunities
          if (aiData.sales_strategy?.upsells && aiData.sales_strategy.upsells.length > 0) {
            aiNotes += '\n\n[UPSELL OPPORTUNITIES]';
            aiData.sales_strategy.upsells.forEach(upsell => {
              aiNotes += `\n• ${upsell}`;
            });
          }
          
          setQuote(prev => ({
            ...prev,
            line_items: lineItems.length > 0 ? lineItems : prev.line_items,
            notes: aiNotes
          }));
          
          setAiDataLoaded(true);
          toast.success('Quote pre-populated with AI analysis!');
          console.log('AI data loaded successfully');
          
        } catch (parseError) {
          console.error('Failed to parse AI data:', parseError);
          toast.error('Could not load AI recommendations');
        }
      }
      
    } catch (error) {
      console.error('Error loading estimate/AI data:', error);
      // Don't show error toast, just fail silently and let user fill manually
    }
  };

  const loadQuote = async (quoteId) => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}`);
      const loadedQuote = response.data;
      
      // Populate the form with loaded quote data
      setQuote({
        quote_number: loadedQuote.quote_number,
        client_name: loadedQuote.client_name,
        client_email: loadedQuote.client_email,
        client_phone: loadedQuote.client_phone,
        client_address: loadedQuote.client_address,
        line_items: loadedQuote.line_items,
        notes: loadedQuote.notes || '',
        attachments: loadedQuote.attachments || [],
        is_rush_job: loadedQuote.is_rush_job || false,
        date_quoted: loadedQuote.date ? new Date(loadedQuote.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      });
      
      setLastSavedQuoteId(quoteId);
      setShowLoadQuotes(false);
      toast.success('Quote loaded successfully!');
    } catch (error) {
      toast.error('Failed to load quote');
    }
  };

  const duplicateQuote = async (quoteId) => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}`);
      const loadedQuote = response.data;
      
      // Load quote but generate new quote number
      await generateQuoteNumber();
      
      setQuote(prev => ({
        ...prev,
        client_name: loadedQuote.client_name,
        client_email: loadedQuote.client_email,
        client_phone: loadedQuote.client_phone,
        client_address: loadedQuote.client_address,
        line_items: loadedQuote.line_items,
        notes: loadedQuote.notes || '',
        attachments: [],
        is_rush_job: loadedQuote.is_rush_job || false,
        date_quoted: new Date().toISOString().split('T')[0], // New date for duplicate
      }));
      
      setLastSavedQuoteId(null);
      setShowLoadQuotes(false);
      toast.success('Quote duplicated! Review and save.');
    } catch (error) {
      toast.error('Failed to duplicate quote');
    }
  };

  const addLineItem = (category) => {
    const newItem = {
      description: '',
      category,
    };

    if (category === 'Labour' || category === 'Design') {
      newItem.hours = 0;
      newItem.rate = 0;
      newItem.employee_id = '';
    } else if (category === 'Materials' || category === 'Shop/Admin') {
      newItem.quantity = 0;
      newItem.unit_cost = 0;
    } else if (category === 'Tools') {
      newItem.tool_id = '';
      newItem.hours = 0;  // Hours used
      newItem.quantity = 1;  // Number of tools
      newItem.unit_cost = 0;  // Rate per hour
    }

    setQuote({
      ...quote,
      line_items: [...quote.line_items, newItem],
    });
  };

  const removeLineItem = (index) => {
    setQuote({
      ...quote,
      line_items: quote.line_items.filter((_, i) => i !== index),
    });
  };

  const updateLineItem = (index, field, value) => {
    const items = [...quote.line_items];
    items[index][field] = value;
    setQuote({ ...quote, line_items: items });
  };

  const calculateTotals = () => {
    if (!costing) return { labour: 0, design: 0, materials: 0, tools: 0, shop_admin: 0, material_surcharge: 0, labour_fee: 0, consumable_supply_fee: 0, disposal_fee: 0, rush_job_fee: 0, subtotal_before_gst: 0, gst_amount: 0, total: 0 };

    // Use the actual costing fields from the API
    const labourRate = costing.labour_rate_per_hour || 90;
    const designRate = costing.design_rate_per_hour || 80;

    let labour = 0;
    let design = 0;
    let materials = 0;
    let tools = 0;
    let shop_admin = 0;

    quote.line_items.forEach((item) => {
      if (item.category === 'Labour' && item.hours && item.rate) {
        labour += item.hours * item.rate;
      } else if (item.category === 'Design' && item.hours && item.rate) {
        design += item.hours * item.rate;
      } else if (item.category === 'Materials' && item.quantity && item.unit_cost) {
        materials += item.quantity * item.unit_cost;
      } else if (item.category === 'Tools') {
        // Tools: Hours × Quantity × Rate per hour
        const hours = item.hours || 0;
        const quantity = item.quantity || 0;
        const rate = item.unit_cost || 0;
        tools += hours * quantity * rate;
      } else if (item.category === 'Shop/Admin' && item.quantity && item.unit_cost) {
        shop_admin += item.quantity * item.unit_cost;
      }
    });

    // Convert percentage rates to decimals (e.g., 15 -> 0.15)
    const materialSurchargeRate = (costing.material_surcharge_rate || 15) / 100;
    const labourBasedRate = (costing.labour_based_rate || 10) / 100;
    const consumableSupplyRate = (costing.consumable_supply_rate || 5) / 100;
    const disposalFeeRate = costing.disposal_fee ? (costing.disposal_fee > 1 ? 0.03 : costing.disposal_fee) : 0.03;
    const rushJobMultiplier = costing.rush_job_multiplier || 1.5;
    const gstRate = (costing.gst_rate || 5) / 100;

    const material_surcharge = materials * materialSurchargeRate;
    const labour_fee = labour * labourBasedRate;
    const consumable_supply_fee = labour * consumableSupplyRate;
    const disposal_fee = materials * disposalFeeRate;
    const rush_job_fee = quote.is_rush_job ? labour * (rushJobMultiplier - 1) : 0;
    
    const subtotal_before_gst = labour + design + materials + tools + shop_admin + 
                                material_surcharge + labour_fee + consumable_supply_fee + 
                                disposal_fee + rush_job_fee;
    const gst_amount = subtotal_before_gst * gstRate;
    const total = subtotal_before_gst + gst_amount;

    return {
      labour,
      design,
      materials,
      tools,
      shop_admin,
      material_surcharge,
      labour_fee,
      consumable_supply_fee,
      disposal_fee,
      rush_job_fee,
      subtotal_before_gst,
      gst_amount,
      total,
    };
  };

  const handleSave = async (createProject = false) => {
    if (!quote.quote_number || !quote.client_name || !quote.client_email || !quote.client_phone) {
      toast.error('Please fill in quote number, client name, email, and phone');
      return;
    }

    setSaving(true);
    try {
      const totals = calculateTotals();
      const quoteData = {
        ...quote,
        labour_subtotal: totals.labour,
        design_subtotal: totals.design,
        materials_subtotal: totals.materials,
        tools_subtotal: totals.tools,
        shop_admin_subtotal: totals.shop_admin,
        material_surcharge: totals.material_surcharge,
        labour_based_fee: totals.labour_fee,
        consumable_supply_fee: totals.consumable_supply_fee,
        disposal_fee: totals.disposal_fee,
        rush_job_fee: totals.rush_job_fee,
        is_rush_job: quote.is_rush_job,
        subtotal_before_gst: totals.subtotal_before_gst,
        gst_amount: totals.gst_amount,
        total: totals.total,
        status: 'Draft',
      };

      // Check capacity and show warning, but ALWAYS save
      let shouldShowCapacityWarning = false;
      
      try {
        const capacityCheck = await axios.post(`${API}/quotes/check-capacity`, quoteData);
        const capacityData = capacityCheck.data;

        // If over capacity, note it for the toast message
        if (capacityData.over_capacity) {
          shouldShowCapacityWarning = true;
          const hoursOver = (capacityData.projected_hours - capacityData.available_hours).toFixed(0);
          
          // Show capacity warning as a toast BEFORE saving
          toast.warning(
            `⚠️ CAPACITY WARNING: This quote adds ${capacityData.labour_hours}h. ` +
            `You'll be ${hoursOver}h over capacity (${capacityData.projected_utilization}%)!`,
            { duration: 8000 }
          );
        }
      } catch (capacityError) {
        console.warn('Could not check capacity, will save anyway:', capacityError);
      }

      // ALWAYS save the quote (no blocking confirm dialog)
      console.log('Saving quote data:', quoteData);
      const response = await axios.post(`${API}/quotes`, quoteData);
      console.log('Quote saved successfully:', response.data);
      
      setLastSavedQuoteId(response.data.id);
      
      if (shouldShowCapacityWarning) {
        toast.success('✅ Quote saved and marked "OUT OF CAPACITY"', { duration: 6000 });
      } else {
        toast.success('Quote saved successfully!');
      }

      if (createProject) {
        await axios.post(`${API}/projects`, {
          quote_id: response.data.id,
          project_name: quote.quote_number,
          client_name: quote.client_name,
          client_email: quote.client_email,
          quote_total: totals.total,
          status: 'Quoted',
        });
        toast.success('Project created from quote!');
      }

      // Reset form and generate new quote number
      setQuote({
        quote_number: '',
        client_name: '',
        client_email: '',
        client_phone: '',
        client_address: '',
        line_items: [],
        notes: '',
        attachments: [],
        is_rush_job: false,
        date_quoted: new Date().toISOString().split('T')[0],
      });
      generateQuoteNumber();
    } catch (error) {
      console.error('Save error:', error);
      console.error('Error details:', error.response?.data);
      
      let errorMsg = 'Failed to save quote.';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          const fields = error.response.data.detail.map(e => e.loc.join('.'));
          errorMsg = `Missing or invalid fields: ${fields.join(', ')}`;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(`❌ ${errorMsg}`, { duration: 10000 });
      console.log('Quote data that failed:', quoteData);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!lastSavedQuoteId) {
      toast.error('Please save the quote first');
      return;
    }

    try {
      toast.info('Generating PDF...');
      
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in again');
        return;
      }
      
      // Create authenticated PDF URL
      const pdfUrl = `${API}/quotes/${lastSavedQuoteId}/pdf?token=${encodeURIComponent(token)}&timestamp=${Date.now()}`;
      
      // Try to open in new tab
      const newTab = window.open(pdfUrl, '_blank');
      
      if (!newTab) {
        // If popup blocked, show the download link
        toast.warn('Popup blocked! Click the PDF link below.');
      } else {
        toast.success('PDF opened in new tab! You can save it from there.');
      }
      
      // Always set the PDF URL for the backup link
      setPdfDownloadUrl({ url: pdfUrl, filename: `quote_${quote.quote_number}.pdf` });
      
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const totals = calculateTotals();

  const handleEmailQuote = async () => {
    if (!lastSavedQuoteId) {
      toast.error('Please save the quote first');
      return;
    }

    if (!quote.client_email) {
      toast.error('Please enter client email address');
      return;
    }

    try {
      toast.info('Sending email...');
      await axios.post(`${API}/quotes/${lastSavedQuoteId}/email`);
      toast.success(`Quote emailed successfully to ${quote.client_email}`);
    } catch (error) {
      console.error('Email error:', error);
      if (error.response?.status === 400) {
        toast.error('Email not configured. Please set up SMTP in Company Settings.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send email');
      }
    }
  };

  const handleConvertToInvoice = async () => {
    if (!lastSavedQuoteId) {
      toast.error('Please save the quote first');
      return;
    }

    setConvertingToInvoice(true);
    try {
      const response = await axios.post(`${API}/invoices/from-quote/${lastSavedQuoteId}`, null, {
        params: { payment_terms: 'Net 15' }
      });
      toast.success('Invoice created successfully! Redirecting...');
      setTimeout(() => {
        navigate('/invoices');
      }, 1500);
    } catch (error) {
      console.error('Convert error:', error.response?.data);
      toast.error('Failed to convert quote to invoice');
    } finally {
      setConvertingToInvoice(false);
    }
  };

  const handleFileUpload = async (e) => {
    if (!lastSavedQuoteId) {
      toast.error('Please save the quote first before uploading files');
      return;
    }

    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFile(true);
    
    try {
      // Upload files one by one
      const uploadedFiles = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API}/quotes/${lastSavedQuoteId}/attachments`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        uploadedFiles.push({ 
          filename: response.data.filename, 
          path: response.data.file_path 
        });
      }

      setQuote(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedFiles]
      }));

      toast.success(`${files.length} file(s) uploaded successfully!`);
      
      // Reset file input
      e.target.value = '';
    } catch (error) {
      toast.error('Failed to upload some files');
    } finally {
      setUploadingFile(false);
    }
  };

  return (
    <div className="p-8" data-testid="quote-builder-page">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2" data-testid="quote-builder-title">
              QUOTE BUILDER
            </h1>
            <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
              Create professional project estimates
            </p>
          </div>
          <Button
            onClick={() => {
              setShowLoadQuotes(!showLoadQuotes);
              if (!showLoadQuotes) loadPreviousQuotes();
            }}
            className="bg-zinc-900 text-white hover:bg-zinc-800 font-mono uppercase tracking-wider rounded-sm shadow-md"
            data-testid="load-quotes-button"
          >
            {showLoadQuotes ? 'Hide Quotes' : 'Load Previous Quote'}
          </Button>
        </div>
      </div>

      {/* Load Previous Quotes Panel */}
      {showLoadQuotes && (
        <Card className="border-border mb-6">
          <CardHeader>
            <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
              Previous Quotes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingQuotes ? (
              <p className="text-center py-8 text-muted-foreground">Loading quotes...</p>
            ) : previousQuotes.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No previous quotes found</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {previousQuotes.map((q) => (
                  <div
                    key={q.id}
                    className="flex items-center justify-between p-4 border border-border rounded-sm hover:border-primary transition-colors"
                    data-testid={`previous-quote-${q.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-bold font-mono text-primary">{q.quote_number}</span>
                        <span className={`status-badge ${
                          q.status === 'Accepted' ? 'bg-green-500/10 text-green-500 border-green-500/20' :
                          q.status === 'Sent' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' :
                          'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                        }`}>
                          {q.status}
                        </span>
                      </div>
                      <p className="text-sm text-foreground">{q.client_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{q.client_email}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-mono font-bold text-lg">${q.total.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {new Date(q.date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => loadQuote(q.id)}
                          size="sm"
                          variant="outline"
                          className="font-mono uppercase text-xs"
                          data-testid={`load-quote-${q.id}`}
                        >
                          Load
                        </Button>
                        <Button
                          onClick={() => duplicateQuote(q.id)}
                          size="sm"
                          variant="ghost"
                          className="font-mono uppercase text-xs"
                          data-testid={`duplicate-quote-${q.id}`}
                        >
                          Duplicate
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Client Information */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Client Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="quote-number" className="uppercase text-xs tracking-wide">Quote Number (Auto-Generated)</Label>
                  <Input
                    id="quote-number"
                    data-testid="quote-number-input"
                    className="mt-1 font-mono bg-muted/50"
                    value={quote.quote_number}
                    readOnly
                    placeholder="Q-2025-001"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Sequential number auto-assigned</p>
                </div>
                <div>
                  <Label htmlFor="date-quoted" className="uppercase text-xs tracking-wide">Date Quoted (Auto-Generated)</Label>
                  <Input
                    id="date-quoted"
                    data-testid="date-quoted-input"
                    className="mt-1 font-mono bg-muted/50"
                    value={quote.date_quoted}
                    readOnly
                    placeholder="YYYY-MM-DD"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Quote creation date</p>
                </div>
                <div>
                  <Label htmlFor="client-name" className="uppercase text-xs tracking-wide">Client Name</Label>
                  <Input
                    id="client-name"
                    data-testid="client-name-input"
                    className="mt-1"
                    value={quote.client_name}
                    onChange={(e) => setQuote({ ...quote, client_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="client-email" className="uppercase text-xs tracking-wide">Email</Label>
                  <Input
                    id="client-email"
                    data-testid="client-email-input"
                    type="email"
                    className="mt-1 font-mono"
                    value={quote.client_email}
                    onChange={(e) => setQuote({ ...quote, client_email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="client-phone" className="uppercase text-xs tracking-wide">Phone Number</Label>
                  <Input
                    id="client-phone"
                    data-testid="client-phone-input"
                    type="tel"
                    className="mt-1 font-mono"
                    placeholder="(555) 123-4567"
                    value={quote.client_phone}
                    onChange={(e) => setQuote({ ...quote, client_phone: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="client-address" className="uppercase text-xs tracking-wide">Address</Label>
                <Input
                  id="client-address"
                  data-testid="client-address-input"
                  className="mt-1"
                  value={quote.client_address}
                  onChange={(e) => setQuote({ ...quote, client_address: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Labour Section */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Labour
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quote.line_items.filter(item => item.category === 'Labour').length === 0 ? (
                <p className="text-muted-foreground text-center py-6 text-sm">No labour items added</p>
              ) : (
                quote.line_items.map((item, index) => item.category === 'Labour' && (
                  <div key={index} className="p-4 border border-border rounded-sm" data-testid={`line-item-${index}`}>
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-xs font-mono uppercase tracking-wide text-primary">Labour Item</span>
                      <button onClick={() => removeLineItem(index)} data-testid={`remove-item-${index}`} className="text-destructive hover:text-destructive/80">
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="text-sm"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <Input type="number" placeholder="Hours" value={item.hours || ''} onChange={(e) => updateLineItem(index, 'hours', parseFloat(e.target.value) || 0)} className="font-mono text-sm" />
                        <Input type="number" placeholder="Rate" value={item.rate || ''} onChange={(e) => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)} className="font-mono text-sm" />
                        <div className="flex items-center justify-end font-mono text-sm">${((item.hours || 0) * (item.rate || 0)).toFixed(2)}</div>
                      </div>
                      {costing && costing.employees && costing.employees.length > 0 && (
                        <select
                          className="w-full px-3 py-2 bg-background border border-input rounded-sm font-mono text-sm"
                          value={item.employee_id || ''}
                          onChange={(e) => {
                            const employee = costing.employees.find(emp => emp.id === e.target.value);
                            updateLineItem(index, 'employee_id', e.target.value);
                            if (employee) {
                              // Auto-fill rate based on Adjusted Shop Rate (what we charge customers)
                              const shopRate = costing.adjusted_shop_rate || 
                                (costing.base_shop_rate * costing.rate_multipliers[costing.selected_rate_level]);
                              updateLineItem(index, 'rate', shopRate);
                            }
                          }}
                        >
                          <option value="">Assign Employee (Optional)</option>
                          {costing.employees.filter(e => e.active).map(emp => (
                            <option key={emp.id} value={emp.id}>{emp.name} - {emp.role}</option>
                          ))}
                        </select>
                      )}
                      <p className="text-xs text-muted-foreground italic">
                        Employee selection is for tracking. Rate uses Adjusted Shop Rate: ${costing ? (costing.adjusted_shop_rate || (costing.base_shop_rate * costing.rate_multipliers[costing.selected_rate_level])).toFixed(2) : '0.00'}/hr
                      </p>
                    </div>
                  </div>
                ))
              )}
              <Button onClick={() => addLineItem('Labour')} data-testid="add-labour-button" variant="outline" size="sm" className="font-mono uppercase text-xs w-full mt-4">
                <Plus size={14} className="mr-1" /> Add Labour
              </Button>
            </CardContent>
          </Card>

          {/* Design Section */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Design / Engineering
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quote.line_items.filter(item => item.category === 'Design').length === 0 ? (
                <p className="text-muted-foreground text-center py-6 text-sm">No design items added</p>
              ) : (
                quote.line_items.map((item, index) => item.category === 'Design' && (
                  <div key={index} className="p-4 border border-border rounded-sm" data-testid={`line-item-${index}`}>
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-xs font-mono uppercase tracking-wide text-primary">Design Item</span>
                      <button onClick={() => removeLineItem(index)} data-testid={`remove-item-${index}`} className="text-destructive hover:text-destructive/80">
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="text-sm"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <Input type="number" placeholder="Hours" value={item.hours || ''} onChange={(e) => updateLineItem(index, 'hours', parseFloat(e.target.value) || 0)} className="font-mono text-sm" />
                        <Input type="number" placeholder="Rate" value={item.rate || ''} onChange={(e) => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)} className="font-mono text-sm" />
                        <div className="flex items-center justify-end font-mono text-sm">${((item.hours || 0) * (item.rate || 0)).toFixed(2)}</div>
                      </div>
                      {costing && costing.employees && costing.employees.length > 0 && (
                        <select
                          className="w-full px-3 py-2 bg-background border border-input rounded-sm font-mono text-sm"
                          value={item.employee_id || ''}
                          onChange={(e) => {
                            const employee = costing.employees.find(emp => emp.id === e.target.value);
                            updateLineItem(index, 'employee_id', e.target.value);
                            if (employee) {
                              // Auto-fill rate based on Adjusted Design Rate (what we charge customers)
                              const shopRate = costing.adjusted_shop_rate || 
                                (costing.base_shop_rate * costing.rate_multipliers[costing.selected_rate_level]);
                              const designRate = costing.adjusted_design_rate || (shopRate + costing.design_uplift);
                              updateLineItem(index, 'rate', designRate);
                            }
                          }}
                        >
                          <option value="">Assign Employee (Optional)</option>
                          {costing.employees.filter(e => e.active).map(emp => (
                            <option key={emp.id} value={emp.id}>{emp.name} - {emp.role}</option>
                          ))}
                        </select>
                      )}
                      <p className="text-xs text-muted-foreground italic">
                        Employee selection is for tracking. Rate uses Adjusted Design Rate: ${costing ? (costing.adjusted_design_rate || ((costing.adjusted_shop_rate || (costing.base_shop_rate * costing.rate_multipliers[costing.selected_rate_level])) + costing.design_uplift)).toFixed(2) : '0.00'}/hr
                      </p>
                    </div>
                  </div>
                ))
              )}
              <Button onClick={() => addLineItem('Design')} data-testid="add-design-button" variant="outline" size="sm" className="font-mono uppercase text-xs w-full mt-4">
                <Plus size={14} className="mr-1" /> Add Design
              </Button>
            </CardContent>
          </Card>

          {/* Materials Section */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Materials
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quote.line_items.filter(item => item.category === 'Materials').length === 0 ? (
                <p className="text-muted-foreground text-center py-6 text-sm">No materials added</p>
              ) : (
                quote.line_items.map((item, index) => item.category === 'Materials' && (
                  <div key={index} className="p-4 border border-border rounded-sm" data-testid={`line-item-${index}`}>
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-xs font-mono uppercase tracking-wide text-primary">Material Item</span>
                      <button onClick={() => removeLineItem(index)} data-testid={`remove-item-${index}`} className="text-destructive hover:text-destructive/80">
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="text-sm"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <Input type="number" placeholder="Qty" value={item.quantity || ''} onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)} className="font-mono text-sm" />
                        <Input type="number" placeholder="Unit Cost" value={item.unit_cost || ''} onChange={(e) => updateLineItem(index, 'unit_cost', parseFloat(e.target.value) || 0)} className="font-mono text-sm" />
                        <div className="flex items-center justify-end font-mono text-sm">${((item.quantity || 0) * (item.unit_cost || 0)).toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <Button onClick={() => addLineItem('Materials')} data-testid="add-materials-button" variant="outline" size="sm" className="font-mono uppercase text-xs w-full mt-4">
                <Plus size={14} className="mr-1" /> Add Material
              </Button>
            </CardContent>
          </Card>

          {/* Tools Section */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Tools & Equipment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quote.line_items.filter(item => item.category === 'Tools').length === 0 ? (
                <p className="text-muted-foreground text-center py-6 text-sm">No tools added</p>
              ) : (
                <>
                  {quote.line_items.map((item, index) => item.category === 'Tools' && (
                    <div key={index} className="p-4 border border-border rounded-sm" data-testid={`line-item-${index}`}>
                      <div className="flex items-start justify-between mb-3">
                        <span className="text-xs font-mono uppercase tracking-wide text-primary">Tool Item</span>
                        <button onClick={() => removeLineItem(index)} data-testid={`remove-item-${index}`} className="text-destructive hover:text-destructive/80">
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div className="space-y-3">
                        {costing && costing.tools && costing.tools.length > 0 && (
                          <select
                            className="w-full px-3 py-2 bg-background border border-input rounded-sm font-mono text-sm"
                            value={item.tool_id || ''}
                            onChange={(e) => {
                              const tool = costing.tools.find(t => t.id === e.target.value);
                              updateLineItem(index, 'tool_id', e.target.value);
                              if (tool) {
                                updateLineItem(index, 'unit_cost', tool.rate);
                                updateLineItem(index, 'description', tool.name);
                              }
                            }}
                          >
                            <option value="">Select Tool</option>
                            {costing.tools.map(tool => (
                              <option key={tool.id} value={tool.id}>{tool.name} - ${tool.rate}/hr ({tool.cost_type})</option>
                            ))}
                          </select>
                        )}
                        <Input
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          className="text-sm"
                        />
                        <div className="grid grid-cols-4 gap-2">
                          <Input 
                            type="number" 
                            placeholder="Hours" 
                            value={item.hours || ''} 
                            onChange={(e) => updateLineItem(index, 'hours', parseFloat(e.target.value) || 0)} 
                            className="font-mono text-sm" 
                          />
                          <Input 
                            type="number" 
                            placeholder="Qty" 
                            value={item.quantity || ''} 
                            onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)} 
                            className="font-mono text-sm" 
                          />
                          <Input 
                            type="number" 
                            placeholder="Rate/hr" 
                            value={item.unit_cost || ''} 
                            onChange={(e) => updateLineItem(index, 'unit_cost', parseFloat(e.target.value) || 0)} 
                            className="font-mono text-sm" 
                          />
                          <div className="flex items-center justify-end font-mono text-sm">
                            ${((item.hours || 0) * (item.quantity || 0) * (item.unit_cost || 0)).toFixed(2)}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Hours × Quantity × Rate = Total Tool Cost
                        </p>
                      </div>
                    </div>
                  ))}
                  {/* Tool Hours Summary */}
                  {(() => {
                    const toolItems = quote.line_items.filter(item => item.category === 'Tools');
                    const totalHours = toolItems.reduce((sum, item) => sum + (item.hours || 0) * (item.quantity || 0), 0);
                    return totalHours > 0 && (
                      <div className="p-3 bg-primary/10 border border-primary/20 rounded-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-mono uppercase tracking-wide">Total Tool Hours:</span>
                          <span className="text-lg font-bold font-mono text-primary">{totalHours.toFixed(1)}h</span>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
              <Button onClick={() => addLineItem('Tools')} data-testid="add-tools-button" variant="outline" size="sm" className="font-mono uppercase text-xs w-full mt-4">
                <Plus size={14} className="mr-1" /> Add Tool
              </Button>
            </CardContent>
          </Card>

          {/* Shop/Admin Section */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Shop / Admin
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quote.line_items.filter(item => item.category === 'Shop/Admin').length === 0 ? (
                <p className="text-muted-foreground text-center py-6 text-sm">No shop/admin items added</p>
              ) : (
                quote.line_items.map((item, index) => item.category === 'Shop/Admin' && (
                  <div key={index} className="p-4 border border-border rounded-sm" data-testid={`line-item-${index}`}>
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-xs font-mono uppercase tracking-wide text-primary">Shop/Admin Item</span>
                      <button onClick={() => removeLineItem(index)} data-testid={`remove-item-${index}`} className="text-destructive hover:text-destructive/80">
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="text-sm"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <Input type="number" placeholder="Qty" value={item.quantity || ''} onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)} className="font-mono text-sm" />
                        <Input type="number" placeholder="Unit Cost" value={item.unit_cost || ''} onChange={(e) => updateLineItem(index, 'unit_cost', parseFloat(e.target.value) || 0)} className="font-mono text-sm" />
                        <div className="flex items-center justify-end font-mono text-sm">${((item.quantity || 0) * (item.unit_cost || 0)).toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <Button onClick={() => addLineItem('Shop/Admin')} data-testid="add-shop-admin-button" variant="outline" size="sm" className="font-mono uppercase text-xs w-full mt-4">
                <Plus size={14} className="mr-1" /> Add Shop/Admin
              </Button>
            </CardContent>
          </Card>

          {/* Design & Resources */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Design & Resources
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="uppercase text-xs tracking-wide">Project Notes</Label>
                <textarea
                  data-testid="quote-notes-textarea"
                  className="w-full p-3 bg-background border border-input rounded-sm font-mono text-sm min-h-[100px] mt-1"
                  placeholder="Additional notes, specifications, or terms..."
                  value={quote.notes}
                  onChange={(e) => setQuote({ ...quote, notes: e.target.value })}
                />
              </div>
              
              <div>
                <Label className="uppercase text-xs tracking-wide mb-2 block">
                  Project Files (Photos, Drawings, PDFs, Specifications)
                </Label>
                <div className="border-2 border-dashed border-border rounded-sm p-6 text-center">
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={!lastSavedQuoteId || uploadingFile}
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf"
                  />
                  <Upload className="mx-auto mb-3 text-muted-foreground" size={32} />
                  <label htmlFor="file-upload">
                    <Button
                      type="button"
                      variant="outline"
                      className="font-mono uppercase"
                      disabled={!lastSavedQuoteId || uploadingFile}
                      onClick={() => document.getElementById('file-upload').click()}
                    >
                      <Plus size={14} className="mr-2" />
                      {uploadingFile ? 'Uploading...' : 'Upload Files'}
                    </Button>
                  </label>
                  <p className="text-xs text-muted-foreground mt-2">
                    {!lastSavedQuoteId 
                      ? 'Save quote first to enable file uploads'
                      : 'Supports: Images, PDFs, CAD files, Documents'}
                  </p>
                </div>

                {quote.attachments.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-2">
                      Uploaded Files ({quote.attachments.length})
                    </p>
                    <div className="space-y-2">
                      {quote.attachments.map((file, index) => {
                        const fileName = file.filename || 'Unknown file';
                        const fileExt = fileName.split('.').pop()?.toLowerCase() || '';
                        
                        const getFileIcon = () => {
                          if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt)) return '🖼️';
                          if (fileExt === 'pdf') return '📄';
                          if (['dwg', 'dxf'].includes(fileExt)) return '📐';
                          if (['doc', 'docx'].includes(fileExt)) return '📝';
                          if (['xls', 'xlsx'].includes(fileExt)) return '📊';
                          return '📎';
                        };

                        return (
                          <div 
                            key={index} 
                            className="flex items-center justify-between p-3 bg-card border border-border rounded-sm hover:border-primary/30 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{getFileIcon()}</span>
                              <div>
                                <p className="text-sm font-medium">{fileName}</p>
                                <p className="text-xs text-muted-foreground font-mono uppercase">{fileExt} file</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs font-mono"
                                onClick={() => {
                                  // Download/view file functionality
                                  toast.info('File viewing coming soon');
                                }}
                              >
                                View
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Duplicate section removed - Design & Resources section above handles this functionality */}
        </div>

        {/* Summary Panel */}
        <div className="space-y-6">
          <Card className="border-border sticky top-8">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">Quote Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Rush Job Toggle */}
              <div className="p-3 bg-card border border-border rounded-sm">
                <label className="flex items-center gap-3 cursor-pointer">
                  <button
                    type="button"
                    onClick={() => setQuote({ ...quote, is_rush_job: !quote.is_rush_job })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-sm transition-colors ${
                      quote.is_rush_job ? 'bg-primary' : 'bg-muted'
                    }`}
                    data-testid="rush-job-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-sm bg-background transition-transform ${
                        quote.is_rush_job ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono uppercase tracking-wide">
                      {quote.is_rush_job ? '⚡ RUSH JOB' : 'Rush Job'}
                    </span>
                    {quote.is_rush_job && (
                      <span className="text-xs text-primary font-mono">
                        +{((costing?.rush_job_fee || 0.25) * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>
                </label>
              </div>

              <div className="space-y-2 pb-3 border-b border-border">
                <div className="flex justify-between text-sm">
                  <span>Labour Subtotal</span>
                  <span className="font-mono">${totals.labour.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Design Subtotal</span>
                  <span className="font-mono">${totals.design.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Materials Subtotal</span>
                  <span className="font-mono">${totals.materials.toFixed(2)}</span>
                </div>
                {totals.tools > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Tools Subtotal</span>
                    <span className="font-mono">${totals.tools.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span>Shop/Admin Subtotal</span>
                  <span className="font-mono">${totals.shop_admin.toFixed(2)}</span>
                </div>
              </div>

              <div className="space-y-2 pb-3 border-b border-border">
                <p className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-2">Surcharges</p>
                <div className="flex justify-between text-sm text-yellow-500">
                  <span>Material Handling (5%)</span>
                  <span className="font-mono">${totals.material_surcharge.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-yellow-500">
                  <span>Shop/Admin Fee (5%)</span>
                  <span className="font-mono">${totals.labour_fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-yellow-500">
                  <span>Consumables/Supply (10%)</span>
                  <span className="font-mono">${totals.consumable_supply_fee.toFixed(2)}</span>
                </div>
                {totals.disposal_fee > 0 && (
                  <div className="flex justify-between text-sm text-yellow-500">
                    <span>Disposal Fee (3%)</span>
                    <span className="font-mono">${totals.disposal_fee.toFixed(2)}</span>
                  </div>
                )}
                {totals.rush_job_fee > 0 && (
                  <div className="flex justify-between text-sm text-orange-500 font-bold">
                    <span>⚡ Rush Job Premium (25%)</span>
                    <span className="font-mono">${totals.rush_job_fee.toFixed(2)}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2 pb-3 border-b border-border">
                <div className="flex justify-between text-sm font-medium">
                  <span>Subtotal Before GST</span>
                  <span className="font-mono">${totals.subtotal_before_gst.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-blue-500">
                  <span>GST (5%)</span>
                  <span className="font-mono">${totals.gst_amount.toFixed(2)}</span>
                </div>
              </div>

              <div className="flex justify-between text-xl font-bold">
                <span className="font-black uppercase">Grand Total</span>
                <span className="font-mono text-primary">${totals.total.toFixed(2)}</span>
              </div>

              <div className="pt-4 space-y-2">
                <Button
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  data-testid="save-quote-button"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider rounded-sm shadow-[0_0_15px_rgba(249,115,22,0.3)] hover:shadow-[0_0_25px_rgba(249,115,22,0.5)]"
                >
                  <Save size={16} className="mr-2" />
                  Save Quote
                </Button>
                <Button
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  data-testid="save-and-create-project-button"
                  variant="outline"
                  className="w-full font-mono uppercase tracking-wider"
                >
                  Save & Create Project
                </Button>
                
                {lastSavedQuoteId && (
                  <div className="pt-3 border-t border-border space-y-2">
                    <p className="text-xs text-muted-foreground text-center font-mono uppercase">Quote Saved</p>
                    <Button
                      onClick={() => setShowVersionHistory(true)}
                      variant="outline"
                      className="w-full font-mono uppercase tracking-wider"
                      data-testid="version-history-button"
                    >
                      <History size={16} className="mr-2" /> Version History
                    </Button>
                    <Button 
                      onClick={handleConvertToInvoice} 
                      disabled={convertingToInvoice}
                      data-testid="convert-to-invoice-button" 
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-mono uppercase tracking-wider shadow-md"
                    >
                      <Receipt size={16} className="mr-2" /> 
                      {convertingToInvoice ? 'Converting...' : 'Convert to Invoice'}
                    </Button>
                    <Button onClick={handleDownloadPDF} data-testid="download-pdf-button" variant="outline" className="w-full font-mono uppercase tracking-wider">
                      <Download size={16} className="mr-2" /> View & Download PDF
                    </Button>
                    
                    {pdfDownloadUrl && (
                      <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-sm">
                        <p className="text-xs text-green-600 font-mono uppercase mb-2 text-center">✓ PDF Ready</p>
                        <div className="space-y-2">
                          <a
                            href={pdfDownloadUrl.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-sm font-mono uppercase text-sm transition-colors"
                            data-testid="pdf-download-link"
                          >
                            <Download size={16} />
                            Open PDF in New Tab
                          </a>
                          
                          <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-sm text-center">
                            <p className="text-xs text-blue-600 font-mono uppercase mb-1">📄 PDF Generated Successfully</p>
                            <p className="text-xs text-muted-foreground">
                              Click "Open PDF in New Tab" above to view your professional quote with:
                            </p>
                            <ul className="text-xs text-muted-foreground mt-1 text-left">
                              <li>• Company branding & contact info</li>
                              <li>• Categorized line items</li>
                              <li>• Custom messages & disclaimers</li>
                              <li>• Professional formatting</li>
                            </ul>
                            <p className="text-xs text-green-600 font-mono mt-2">
                              Use Ctrl+S (Cmd+S) to save the PDF
                            </p>
                          </div>
                        </div>
                        
                        <p className="text-xs text-muted-foreground text-center mt-2">
                          {pdfDownloadUrl.filename}
                        </p>
                      </div>
                    )}
                    
                    <Button onClick={handleEmailQuote} data-testid="email-quote-button" variant="outline" className="w-full font-mono uppercase tracking-wider">
                      <Mail size={16} className="mr-2" /> Email to Client
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Version History Modal */}
      {showVersionHistory && lastSavedQuoteId && (
        <QuoteVersionHistory
          quoteId={lastSavedQuoteId}
          currentQuote={quote}
          onVersionRestored={() => {
            // Reload the quote after restoration
            window.location.reload();
          }}
          onClose={() => setShowVersionHistory(false)}
        />
      )}
    </div>
  );
};

export default QuoteBuilder;
